import { useCopyToClipboard } from "react-use";
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { Notify } from "@flexisaf/flexibull2/build/notify";
import { Box, Spacer } from "@flexisaf/flexibull2/build/layout";
import { Text } from "@flexisaf/flexibull2/build/typo";
import { Button } from "@flexisaf/flexibull2/build/button";

import {
    useCreateProductIntegrationMutation,
    useUpdateIntegrationRequestMutation,
    useRequestEditIntegrationMutation,
} from "@/api/productIntegration";
import { Stepper } from "@/components";
import { IconCopy } from "@/components/icons";
import { useViewMode } from "@/utils/hooks/useViewMode";
import { theme } from "@/style";
import { formatCurrency, hasItems } from "@/utils/help";
import { productOptions } from "@/utils/uiOptions";
import { viewModes } from "@/utils/constants";
import { snakeCaseToCapital } from "@/utils/help";
import { useStepper } from "@/components/stepper/Stepper";
import { splitTypes } from "@/utils/constants";

import { ContentBaseWrapper } from "./productIntegrationStyles";
import {
    useProductIntegration,
    transformProductIntegration,
    transformProductIntegrationToRequestEditDto,
} from "../productIntegrationUtils";
import { useNavigate } from "react-router-dom";
import { productIntegrationRoutes } from "@/routes/allRoutes";
import { formatNumber } from "@/mock/utils";

const iconCopy = <IconCopy width="1.2em" height="1.2em" stroke={theme.PrimaryGrey} />;
const getBeneficiaryColumns = (splitType) => [
    "Name",
    "Bank Name",
    splitType === splitTypes.PERCENTAGE ? "Percentage(%)" : "Amount(₦)",
];

export default function IntegrationSummary() {
    const { values, isEditMode, requestId, requestStatus } = useProductIntegration();
    const { handlePrev } = useStepper();

    const [createProductIntegration, { isLoading: isCreating, error: submissionError, data }] =
        useCreateProductIntegrationMutation();
    const [
        updateIntegrationRequest,
        { isLoading: isUpdating, error: updateError, data: updateData },
    ] = useUpdateIntegrationRequestMutation();
    const [
        requestEditIntegration,
        { isLoading: isRequestEditLoading, error: requestEditError, data: requestEditData },
    ] = useRequestEditIntegrationMutation();
    const [apiError, setApiError] = useState();
    const navigate = useNavigate();

    useEffect(() => {
        if (submissionError || updateError || requestEditError)
            setApiError(submissionError || updateError || requestEditError);
    }, [submissionError, updateError, requestEditError]);

    useEffect(() => {
        if (isEditMode) return;
        if (data) {
            navigate(`${productIntegrationRoutes.index}`, { state: { spotlightId: data.id } });
        }
    }, [data, isEditMode, navigate]);

    useEffect(() => {
        if (!isEditMode) return;
        if (updateData || requestEditData) {
            navigate(`${productIntegrationRoutes.index}`);
        }
    }, [isEditMode, navigate, updateData, requestEditData]);

    const viewMode = useViewMode();
    const isMobile = viewMode === viewModes.MOBILE;

    const parsedValues = useMemo(() => {
        const {
            schoolId,
            institutionId,
            productId,
            description,
            charge = 0,
            beneficiaries,
            paymentFormFile,
            firstName,
            lastName,
            jobTitle,
            email,
            phoneNumber,
            schoolName,
            customerName,
            ...restValues
        } = values;
        const productName =
            productOptions.find((product) => product.value === productId)?.label || "";

        const formattedBeneficiaries = beneficiaries?.map(({ name, bank, amount, bearer }) => ({
            name,
            bankName: bank.name,
            amount: formatNumber(amount),
            bearer,
        }));
        return {
            ...restValues,
            beneficiaries: formattedBeneficiaries,
            charge: formatCurrency(charge),
            schoolId,
            institutionId,
            schoolName: schoolName || "",
            customerName: customerName || "",
            description: description || "",
            productName,
            paymentFormFile,
            firstName,
            lastName,
            jobTitle,
            email,
            phoneNumber,
        };
    }, [values]);

    const message = useMemo(() => {
        if (apiError)
            return (
                <Text block color={theme.PrimaryRed}>
                    {apiError.message}
                </Text>
            );
        if (data) return <IntegrationIdPreviewer id={data.id} />;

        return null;
    }, [apiError, data]);

    const isSubmitting = isCreating || isUpdating || isRequestEditLoading;

    return (
        <ContentBaseWrapper>
            <div className="content">
                <Section title="Customer Details">
                    <Field
                        title="Institution"
                        value={`${parsedValues.schoolName} (${parsedValues.institutionId || "N/A"})`}
                    />
                    <Field
                        title="Customer"
                        value={`${parsedValues.customerName || "N/A"} (${parsedValues.schoolId})`}
                    />
                    <Field title="Product" value={parsedValues.productName} />
                    <Field title="Description" value={parsedValues.description || "N/A"} />
                </Section>

                {(parsedValues.firstName ||
                    parsedValues.lastName ||
                    parsedValues.email ||
                    parsedValues.phoneNumber ||
                    parsedValues.jobTitle) && (
                    <>
                        <Spacer space="40" />
                        <Section title="Contact Information">
                            {parsedValues.firstName && (
                                <Field title="First Name" value={parsedValues.firstName} />
                            )}
                            {parsedValues.lastName && (
                                <Field title="Last Name" value={parsedValues.lastName} />
                            )}
                            {parsedValues.jobTitle && (
                                <Field title="Job Title" value={parsedValues.jobTitle} />
                            )}
                            {parsedValues.email && (
                                <Field title="Email" value={parsedValues.email} />
                            )}
                            {parsedValues.phoneNumber && (
                                <Field title="Phone Number" value={parsedValues.phoneNumber} />
                            )}
                        </Section>
                    </>
                )}

                <Spacer space="40" />

                <Section title="Transaction Setup">
                    <Field title="Merchant" value={snakeCaseToCapital(parsedValues.platform)} />
                    <Field title="Charge (₦)" value={parsedValues.charge} />
                    {parsedValues.splitType && (
                        <Field
                            title="Type"
                            value={` ${snakeCaseToCapital(parsedValues.splitType)} Split`}
                        />
                    )}
                </Section>
                <Spacer space="40" />
                {hasItems(parsedValues.beneficiaries) && (
                    <Section title="Beneficiaries" gap={`2em ${isMobile ? "4em" : null} `}>
                        <BeneficiariesTable
                            beneficiaries={parsedValues.beneficiaries}
                            columns={getBeneficiaryColumns(parsedValues.splitType)}
                        />
                    </Section>
                )}
                {parsedValues.paymentFormFile && (
                    <>
                        <Spacer space="40" />
                        <Section title="Payment Form">
                            <PaymentFormDisplay file={parsedValues.paymentFormFile} />
                        </Section>
                    </>
                )}
                <Spacer space="40" />
                {message}
            </div>
            <Stepper.StepButtons
                onPrev={() => {
                    setApiError(null);
                    handlePrev();
                }}
                isSubmitting={isSubmitting}
                onSubmit={() => {
                    if (isSubmitting) return;
                    if (isEditMode) {
                        if (
                            requestStatus?.toUpperCase() === "APPROVED" ||
                            requestStatus?.toUpperCase() === "COMPLETED" ||
                            requestStatus?.toUpperCase() === "PENDING_EDIT"
                        ) {
                            requestEditIntegration({
                                id: requestId,
                                setupDto: transformProductIntegrationToRequestEditDto(values),
                                file: values.paymentFormFile,
                            });
                        } else {
                            const payload = transformProductIntegration(values);

                            updateIntegrationRequest({
                                id: requestId,
                                requestStatus,
                                ...payload,
                            });
                        }
                        return;
                    }
                    createProductIntegration(transformProductIntegration(values));
                }}
            />
        </ContentBaseWrapper>
    );
}

function Section(props) {
    const { title, children } = props;
    return (
        <section>
            <Text
                block
                size="1.2em"
                bold
                style={{
                    paddingBottom: "1em",
                    borderBottom: `.5px solid ${theme.PrimaryGrey}`,
                    marginBottom: "2em",
                }}
            >
                {title}
            </Text>
            <Box>{children}</Box>
        </section>
    );
}

Section.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node,
};

function Field(props) {
    const { title, value } = props;
    const isStringTitle = typeof title === "string";
    return (
        <Box display="flex" style={{ marginBottom: "1em" }}>
            {isStringTitle ? (
                <Box display="inline-block" width="7em" style={{ fontWeight: "bold" }}>
                    {title}:
                </Box>
            ) : (
                title
            )}
            <Box display="inline-block">{value}</Box>
        </Box>
    );
}

Field.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

function BeneficiariesTable({ beneficiaries = [], columns }) {
    return (
        <table style={{ textAlign: "left", width: "100%" }}>
            <thead>
                <tr style={{ color: theme.PrimaryGrey, marginBottom: "2em" }}>
                    {columns.map((col) => (
                        <th key={col}> {col} </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {beneficiaries.map(({ name, bankName, amount }, index) => (
                    <tr key={index} style={{ marginBottom: "1em" }}>
                        <td style={{ padding: "4px 0px" }}> {name}</td>
                        <td>{bankName}</td> <td> {amount}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

BeneficiariesTable.propTypes = {
    beneficiaries: PropTypes.objectOf({
        name: PropTypes.string,
        bankName: PropTypes.string,
        amount: PropTypes.string,
    }).isRequired,
    columns: PropTypes.array,
};

function IntegrationIdPreviewer(props) {
    const { id } = props;
    const [, copyHandler] = useCopyToClipboard();

    const onCopy = () => {
        copyHandler(id);
        Notify(`Integration ID "${id}" copied to clipboard`, {
            position: "top-right",
        });
    };
    return (
        <Box>
            <Text>
                <span style={{ color: theme.PrimaryGreen }}>Integration created successfully.</span>
                <br />
                <br /> The Integration ID is returned below
            </Text>
            <Spacer space={8} />
            <Box display="flex" style={{ alignItems: "center" }}>
                <Box pad=" .8em 1em" style={{ border: `1px solid ${theme.SecondaryGrey}` }}>
                    <Text color={theme.PrimaryGrey} bold>
                        {id}
                    </Text>
                </Box>
                <Button spaceLeft plain onClick={onCopy} title="Copy Integration ID">
                    {iconCopy}
                </Button>
            </Box>
        </Box>
    );
}

IntegrationIdPreviewer.propTypes = {
    id: PropTypes.string,
};

function PaymentFormDisplay({ file }) {
    const isImage = file?.type?.startsWith("image/");
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        if (isImage && file?.url) {
            setPreviewUrl(file.url);
            return;
        }
        if (isImage && file) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [isImage, file]);

    return (
        <Box>
            <Field title="File Name" value={file?.name || "N/A"} />
            <Field title="File Type" value={file?.type || "N/A"} />
            <Field
                title="File Size"
                value={file?.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "N/A"}
            />
            {isImage && previewUrl && (
                <Box margin="1em 0">
                    <Text bold style={{ marginBottom: "0.5rem" }}>
                        Preview:
                    </Text>
                    <Box
                        style={{
                            maxWidth: "300px",
                            maxHeight: "200px",
                            border: `1px solid ${theme.SecondaryGrey}`,
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <img
                            src={previewUrl}
                            alt="Payment form preview"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                    </Box>
                </Box>
            )}
            {!isImage && (
                <Box margin="1em 0">
                    <Text>📕 PDF File</Text>
                </Box>
            )}
        </Box>
    );
}

PaymentFormDisplay.propTypes = {
    file: PropTypes.object,
};
